import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import { api, ApiError, jsonBody } from "./api";
import type { Account, WebConfig } from "./types";

interface AuthContextValue {
  account: Account | null;
  config: WebConfig | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  register(displayName: string, email: string, password: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [account, setAccount] = useState<Account | null>(null);
  const [config, setConfig] = useState<WebConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await api<{ account: Account }>("/v1/web/auth/session");
      setAccount(result.account);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setAccount(null);
        return;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([
      api<WebConfig>("/v1/web/config"),
      refresh().catch(() => undefined),
    ])
      .then(([webConfig]) => {
        if (active) {
          setConfig(webConfig);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    account,
    config,
    loading,
    login: async (email, password) => {
      const result = await api<{ account: Account }>("/v1/web/auth/login", {
        body: jsonBody({ email, password }),
        method: "POST",
      });
      setAccount(result.account);
    },
    logout: async () => {
      await api<void>("/v1/web/auth/logout", { method: "POST" });
      setAccount(null);
    },
    refresh,
    register: async (displayName, email, password) => {
      const result = await api<{ account: Account }>("/v1/web/auth/register", {
        body: jsonBody({ displayName, email, password }),
        method: "POST",
      });
      setAccount(result.account);
    },
  }), [account, config, loading, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return value;
}
